import { useMutation } from "@apollo/client/react";
import { useLocalSearchParams } from "expo-router";
import Head from "expo-router/head";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";

import type { DisplayEvent } from "@/utils/event-helpers";

import { eventFamily } from "@/atoms/event";
import { eventTicketKindIdFamily } from "@/atoms/event-form";
import { currentUserIdAtom } from "@/atoms/session";
import { BackgroundImageBlur } from "@/components/BackgroundImageBlur";
import { EventDisplay } from "@/components/event/event-display";
import { GText } from "@/components/g-text";
import { colors } from "@/constants/colors";
import { REPLACE_TICKET_KIND_MUTATION } from "@/graphql/event-mutations";
import { useEventDetail } from "@/hooks/use-event-detail";
import { useEventEdit } from "@/hooks/use-event-edit";
import { useEventMembers } from "@/hooks/use-event-members-sync";
import { usePayment } from "@/hooks/use-payment";
import { toDisplayEvent } from "@/utils/event-helpers";

export default function EventScreen() {
  const { mode, slug } = useLocalSearchParams<{
    mode?: string;
    slug: string;
  }>();
  const isEditing = mode === "edit";
  const userId = useAtomValue(currentUserIdAtom);

  const [event, setEvent] = useAtom(eventFamily(slug));
  const [, setTicketKindId] = useAtom(eventTicketKindIdFamily(slug));
  const [flyerUrl, setFlyerUrl] = useState<null | string>(null);

  const { error, eventRow, loading, node: eventNode } = useEventDetail(slug);

  // Sync DisplayEvent atom + ticket kind ID from query result
  useEffect(() => {
    if (!eventRow) return;
    setEvent(toDisplayEvent(eventRow, userId));
    const tier = eventRow.event_ticket_kind?.[0];
    setTicketKindId(tier?.id ?? null);
    if (eventRow.flyer_url)
      setFlyerUrl(eventRow.flyer_url.replace(/^http:\/\//i, "https://"));
  }, [eventRow, setEvent, setTicketKindId, userId]);

  const eventId = event?.id ?? null;
  const { guests, hosts, myRsvp, refreshMember } = useEventMembers(
    eventId ?? "",
    userId,
  );

  const eventEdit = useEventEdit(slug, eventId);

  const [replaceTicketKind] = useMutation(REPLACE_TICKET_KIND_MUTATION);

  const handleFlyerUrlChange = useCallback((url: string) => {
    setFlyerUrl(url);
  }, []);

  const handleRemoveTicket = useCallback(async () => {
    if (!eventId) return;
    const { data } = await replaceTicketKind({
      variables: {
        eventId,
        newTicket: { event_id: eventId, price_cents: 0 },
      },
    });
    const newId =
      data?.insertIntoevent_ticket_kindCollection?.records[0]?.id ?? null;
    setTicketKindId(newId);
    eventEdit.patchTicketKind({ price_cents: 0 });
  }, [eventEdit, eventId, replaceTicketKind, setTicketKindId]);

  // Payment hook
  const paymentEvent = event ?? ({} as DisplayEvent);
  const {
    loading: paymentLoading,
    paymentUI,
    showSuccess,
    startPayment,
  } = usePayment(paymentEvent, myRsvp?.rsvp_status ?? null);

  if (loading) {
    return (
      <View style={styles.container}>
        <BackgroundImageBlur displayFlyerUrl={flyerUrl} />
        <View style={[styles.fill, styles.center]}>
          <ActivityIndicator color={colors.white} size="large" />
        </View>
      </View>
    );
  }

  if (error || !event || !eventNode) {
    return (
      <View style={styles.container}>
        <BackgroundImageBlur displayFlyerUrl={flyerUrl} />
        <View style={[styles.fill, styles.center]}>
          <GText style={styles.errorText}>whoops!</GText>
          <GText style={styles.errorText}>{error ?? "event not found"}</GText>
        </View>
      </View>
    );
  }

  const truncatedDescription = event.description
    ? event.description.slice(0, 160)
    : undefined;

  return (
    <View style={styles.container}>
      <BackgroundImageBlur displayFlyerUrl={flyerUrl} />

      {!isEditing && (
        <Head>
          {event.title ? <title>{event.title}</title> : null}
          {event.title ? (
            <meta content={event.title} property="og:title" />
          ) : null}
          {event.title ? (
            <meta content={event.title} name="twitter:title" />
          ) : null}
          {truncatedDescription ? (
            <meta content={truncatedDescription} name="description" />
          ) : null}
          {truncatedDescription ? (
            <meta content={truncatedDescription} property="og:description" />
          ) : null}
          {event.flyer_url ? (
            <meta content={event.flyer_url} property="og:image" />
          ) : null}
          {truncatedDescription ? (
            <meta content={truncatedDescription} name="twitter:description" />
          ) : null}
          {event.flyer_url ? (
            <meta content={event.flyer_url} name="twitter:image" />
          ) : null}
          <meta content="summary_large_image" name="twitter:card" />
        </Head>
      )}

      <EventDisplay
        event={eventNode}
        eventEdit={eventEdit}
        guests={guests}
        hosts={hosts}
        mode={isEditing ? "edit" : "view"}
        myRsvp={myRsvp}
        onFlyerUrlChange={handleFlyerUrlChange}
        onGetTicket={startPayment}
        onRefreshMember={refreshMember}
        onRemoveTicket={handleRemoveTicket}
        onShareEvent={showSuccess}
        paymentUI={paymentUI}
        ticketLoading={paymentLoading}
        viewEvent={event}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  center: {
    alignItems: "center",
    justifyContent: "center",
  },
  container: {
    backgroundColor: colors.transparent,
    flex: 1,
  },
  errorText: {
    color: colors.light,
    fontSize: 18,
  },
  fill: {
    flex: 1,
  },
});
